import dayjs from 'dayjs';
import queryString from 'query-string';

import axios from '../http-common';

import { APICurrentImagesType, CurrentImagesTabType, SamplesCurrentData } from '@/types/current-data';
import { ChartType, ChartSummary, LineData, SamplesTableType, APISamplesType } from '@/types/sample';

const fixedValues = [
  'grm_total',
  'weight_sum',
  'weight_avg',
  'gaping_percentage',
  'gaping_weight',
  'melanin_percentage',
  'melanin_weight',
  'hemat_percentage',
  'hemat_weight',
  'overgrowth_percentage',
  'overgrowth_weight',
  'pigment_avg',
  'pigment_outlr_percentage',
  'perr',
  'gate_l'
];

export const getCurrentData = async (startDate: number, endDate: number): Promise<SamplesTableType[]> => {
  try {
    const { data } = await axios.get<APISamplesType[]>(
      `/todays?ts1=${startDate}&ts2=${endDate}&grm,lot,batch,numpalet,station,gname,extnumord`
    );
    return data.map(({ timestamp, ...others }) => ({
      ...Object.entries(others).reduce((prev, [key, value]) => ({
        ...prev,
        ...(fixedValues.includes(key)
          ? {
            [key]: value !== null
              ? Number(value).toFixed(2)
                .replace('.', ',')
              : '-'
          }
          : { [key]: value })
      }), {
        key: timestamp,
        date: dayjs(timestamp).format('DD.MM.YYYY HH:mm')
      })
    })) as SamplesTableType[];
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getCurrentImages = async (
  startDate: number,
  endDate: number,
  token?: number | null,
  noImages?: boolean,
  limit?: number
): Promise<CurrentImagesTabType> => {
  const customQuery = queryString.stringify({
    ts1: startDate,
    ts2: endDate,
    token,
    limit,
    no_images: noImages
  }, { sort: false, arrayFormat: 'separator' });

  try {
    const { data } = await axios.get<APICurrentImagesType>(`/todays/images?${customQuery}`);
    return {
      ...data,
      images: data.images
        ? data.images.map(({ ts, bucket_image }) => ({
          id: ts,
          src: bucket_image
        }))
        : [],
      totalCount: data.totalCount ? data.totalCount : 0
    };
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getCurrentImagesTotalCount = async (startDate: number, endDate: number): Promise<CurrentImagesTabType['totalCount']> => {
  try {
    const { data } = await axios.get<Pick<CurrentImagesTabType, 'totalCount'>>(`/todays/images/total-count?ts1=${startDate}&ts2=${endDate}`);
    return data.totalCount;
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getCurrentChartData = async (
  startDate: number,
  endDate: number,
  selectedChart: string,
  singleFieldValue?: string[]
): Promise<ChartType> => {
  const customQuery = queryString.stringify({
    ts1: startDate,
    ts2: endDate,
    value: singleFieldValue
  }, { sort: false, arrayFormat: 'separator' });

  try {
    const { data } = await axios.get<ChartType>(`/todays/charts/${selectedChart}?${customQuery}`);
    return data;
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getCurrentTimelineData = async (
  startDate: number,
  endDate: number,
  value: string
): Promise<ChartType> => {
  const customQuery = queryString.stringify({
    ts1: startDate,
    ts2: endDate,
    value
  }, { sort: false, arrayFormat: 'separator' });

  try {
    const { data } = await axios.get<ChartType>(`/todays/times?${customQuery}`);
    return data;
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getSummaryData = async (
  startDate: number,
  endDate: number,
  interval: number
): Promise<LineData[]> => {
  const customQuery = queryString.stringify({
    ts1: startDate,
    ts2: endDate,
    interval
  }, { sort: false, arrayFormat: 'separator' });

  const summary: LineData[] = [
    {
      id: 'gaping',
      data: []
    },
    {
      id: 'overgrowth',
      data: []
    },
    {
      id: 'melanin',
      data: []
    },
    {
      id: 'hemat',
      data: []
    }
  ];

  try {
    const { data } = await axios.get<ChartSummary[]>(`/todays/summary?${customQuery}`);

    data.forEach((element) => {
      summary.forEach((value, index) => {
        summary[index].data.push({ x: dayjs(element.ts).format('YYYY-MM-DD HH:mm'), y: element[value.id as keyof ChartSummary] });
      });
    });

    return summary;
  } catch (error) {
    throw new Error((error as Error).message);
  }
};

export const getSamplesCurrentData = async (
  startDate: number,
  endDate: number
): Promise<SamplesCurrentData> => {
  const customQuery = queryString.stringify({
    ts1: startDate,
    ts2: endDate
  }, { sort: false, arrayFormat: 'separator' });

  try {
    const { data } = await axios.get<SamplesCurrentData>(`/todays/last?${customQuery}`);
    return data;
  } catch (error) {
    throw new Error((error as Error).message);
  }
};
